/**
 * Navigation tracking component
 * Tracks clicks on top menu, inner menu, and footer
 */
import { pushTopMenuEvent, pushFooterEvent } from '../utils/dataLayer';
import { getAnalyticsSection } from '../utils/analyticsContext';

const TOP_MENU_SELECTOR = [
  '.nav__language-link',
  '.nav__account-label',
  '.nav-secondary__item',
  '.nav__logo-link',
  '.nav__hamburger-button',
  '.nav-menu__menu-item',
].join(',');

const FOOTER_SELECTOR = [
  '.footer__nav-link',
  '.footer__social-link',
].join(',');

const getElementLabel = (element) => {
  if (element.matches('.nav__language-link')) {
    // eslint-disable-next-line no-use-before-define
    return getLanguageLabel(element);
  }

  if (element.matches('.nav__logo-link')) {
    return 'Logo';
  }

  if (element.matches('.nav__hamburger-button')) {
    return element.getAttribute('aria-pressed') === 'true' ? 'Close Menu' : 'Open Menu';
  }

  const socialIcon = element.querySelector('.footer__social-icon');
  if (socialIcon) {
    const label = socialIcon.alt || 'Social';
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  return element.textContent.replace(/\s+/g, ' ').trim()
    || element.getAttribute('aria-label')
    || '';
};

/**
 * Get label for language switcher
 * @param {HTMLElement} link - Language link element
 * @returns {string} Language label
 */
function getLanguageLabel(link) {
  const langCode = link.dataset.languageCode;
  return langCode ? langCode.toUpperCase() : 'Language';
}

/**
 * Initialize top menu tracking
 */
function initTopMenuTracking() {
  document.addEventListener('click', (event) => {
    const target = event.target?.closest?.(TOP_MENU_SELECTOR);
    if (!target) {
      return;
    }

    pushTopMenuEvent({
      section: getAnalyticsSection() || 'Unknown',
      label: getElementLabel(target),
    });
  });
}

/**
 * Initialize footer tracking
 */
function initFooterTracking() {
  document.addEventListener('click', (event) => {
    const target = event.target?.closest?.(FOOTER_SELECTOR);
    if (!target) {
      return;
    }

    pushFooterEvent({
      section: getAnalyticsSection() || 'Unknown',
      label: getElementLabel(target),
    });
  });
}

/**
 * Initialize navigation tracking
 */
export default function initNavigationTracking() {
  function init() {
    initTopMenuTracking();
    initFooterTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
